import { ConfirmationModal, CustomButton, CustomChip, CustomDrawer, SkeletonLoading } from '@components';
import { ClientOrganization, Invoice, OrganizationBilling } from '@graphql';
import AddCardOutlinedIcon from '@mui/icons-material/AddCardOutlined';
import { useContext, useEffect, useMemo, useState } from 'react';
import { COLORS } from '@styles';
import { Typography } from '@mui/material';
import { CreditCard } from '@mui/icons-material';
import RetryIconPath from '@assets/svg/Return.svg';
import { appContext, formatMoney } from '@utils';
import { observer } from 'mobx-react';
import { RequestStatus } from '@store';
import { OrganizationInfo } from '../OrganizationInfo';
import { InvoiceDetails } from '../InvoiceDetails';

interface IRetryPaymentDrawerProps {
  open: boolean;
  onClose: () => void;
  organization?: ClientOrganization;
  invoice?: Invoice | null;
  organizationBilling?: OrganizationBilling;
  error: boolean;
  message?: string;
}

const styles = {
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '85vh',
  } as React.CSSProperties,
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    padding: '0 1rem',
    gap: '1.5rem',
  } as React.CSSProperties,
  button: {
    padding: '1.5rem 1rem 0 1rem',
    borderTop: `1px solid ${COLORS.NEUTRAL_DARK_2}`,
    marginTop: 'auto',
  },
  creditCardContainer: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  creditCardContentContainer: {
    display: 'flex',
    gap: '1rem',
    justifyContent: 'flex-start',
    alignItems: 'center',
  } as React.CSSProperties,
  creditCardIconContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.5625rem',
    borderRadius: '1.5rem',
    backgroundColor: COLORS.NEUTRAL_DARK_2,
    color: COLORS.NEUTRAL_LIGHT_0,
  } as React.CSSProperties,
  creditCardItem: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  creditCardNumber: {
    fontSize: '0.875rem',
    fontWeight: 600,
  } as React.CSSProperties,
  creditCardFailedReason: {
    fontSize: '0.75rem',
  } as React.CSSProperties,
  creditCardAttemptCount: {
    fontSize: '0.75rem',
    color: COLORS.NEUTRAL_LIGHT_0,
  } as React.CSSProperties,
  error: {
    fontSize: '0.875rem',
    color: COLORS.NEGATIVE_NEUTRAL,
  } as React.CSSProperties,
}

export const RetryPaymentDrawer = observer((
  {
    open,
    onClose,
    organization,
    invoice,
    organizationBilling,
    error,
    message,
  }: IRetryPaymentDrawerProps) => {
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const {
    billingStore: { manualPayInvoice, getInvoices, getPaymentIntent, paymentIntentData }
  } = useContext(appContext);

  useEffect(() => {
    if (invoice?.paymentIntentId && open) {
      getPaymentIntent(invoice?.paymentIntentId as string);
    }
  }, [invoice?.paymentIntentId, getPaymentIntent, open]);

  const handleCloseConfirmationModal = () => setIsOpenConfirmationModal(false);
  const handleOpenConfirmationModal = () => setIsOpenConfirmationModal(true);

  const onSubmit = () => {
    handleOpenConfirmationModal();
  }

  const onConfirm = async () => {
    const manualPayInvoiceResponse = await manualPayInvoice(invoice?.id as string, organization?.id as string);

    if (manualPayInvoiceResponse) {
      handleCloseConfirmationModal();
      onClose();
      getInvoices(organization?.id as string);
    }
  };

  const {
    creditCardNumber,
    failedReason,
    attemptCount,
  } = useMemo(() => ({
    creditCardNumber: paymentIntentData.data?.paymentMethod.last4,
    failedReason: paymentIntentData.data?.error.message,
    attemptCount: invoice?.attemptCount || 0,
  }), [paymentIntentData.data, invoice?.attemptCount]);

  return (
    <>
      <CustomDrawer
        open={open}
        onClose={onClose}
        headerLabel="Retry Payment"
        headerIcon={<AddCardOutlinedIcon />}
      >
        <div style={styles.formWrapper}>
          <div style={styles.formContainer}>
            <OrganizationInfo organization={organization} />
            <InvoiceDetails invoice={invoice} organizationBilling={organizationBilling} />

            <div style={styles.creditCardContainer}>
              <SkeletonLoading isLoading={paymentIntentData.status === RequestStatus.LOADING}>
                <div style={styles.creditCardContentContainer}>
                  <div style={styles.creditCardIconContainer}>
                    <CreditCard />
                  </div>
                  <div style={styles.creditCardItem}>
                    <Typography variant='body1' style={styles.creditCardNumber}>{`•••• ${creditCardNumber}`}</Typography>
                    <Typography variant='body1' style={styles.creditCardFailedReason}>{failedReason}</Typography>
                    <Typography variant='body1' style={styles.creditCardAttemptCount}>{`${attemptCount} attempts`}</Typography>
                  </div>
                </div>
                <CustomChip variant="error" label="Declined" />
            </SkeletonLoading>
            </div>

            <div style={styles.button}>
              <CustomButton variant="default" onClick={onSubmit}>
                Retry Payment Now
              </CustomButton>
            </div>
          </div>
        </div>
      </CustomDrawer>
      <ConfirmationModal
        icon={<img src={RetryIconPath} alt="retry-icon" />}
        title={`Are you sure you want to retry charge ${organization?.name}?`}
        onClose={handleCloseConfirmationModal}
        isOpen={isOpenConfirmationModal}
        variant="default"
        onConfirm={onConfirm}
        confirmationButtonLabel="Confirm New Attempt"
        confirmationText={organization?.name || ''}
        hasError={error}
        message={message}
      >
        <p>
          This will immediately charge the customer&apos;s payment method with the amount of {' '}
          <strong>{formatMoney(invoice?.amountRemaining || 0)}</strong>.
        </p>
        <p>To proceed with the new charge attempt, please type the organization name: </p>
        <strong>{organization?.name || ''}</strong>
      </ConfirmationModal>
    </>
  );
});
